import { useEffect, useState } from "react";
import { http, formatApiErrorDetail } from "../../lib/api";

export default function ImageOfDayTab() {
  const [candidates, setCandidates] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [caption, setCaption] = useState("");
  const [filter, setFilter] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/image-candidates", { params: { hours: 36 } });
      setCandidates(data.candidates || []);
      setCurrentId(data.current_article_id || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setIt = async (a) => {
    setSavingId(a.id); setMsg(""); setErr("");
    try {
      await http.put("/admin/image-of-day", { article_id: a.id, caption: a.id === currentId ? caption : "" });
      setCurrentId(a.id);
      setMsg(`Imagen del día actualizada: ${a.title.slice(0, 60)}…`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingId(null);
    }
  };

  const updateCaption = async () => {
    if (!currentId) return;
    setSavingId(currentId); setMsg(""); setErr("");
    try {
      await http.put("/admin/image-of-day", { article_id: currentId, caption });
      setMsg("Pie de foto actualizado.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingId(null);
    }
  };

  const clearIt = async () => {
    if (!window.confirm("¿Quitar la imagen del día?")) return;
    await http.delete("/admin/image-of-day");
    setCurrentId(null); setCaption("");
    setMsg("Imagen del día removida.");
  };

  const enrich = async () => {
    setEnriching(true); setMsg(""); setErr("");
    try {
      const { data } = await http.post("/admin/enrich-images", null, { params: { limit: 80 } });
      setMsg(`Imágenes nuevas extraídas: ${data.enriched}`);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setEnriching(false);
    }
  };

  const filtered = filter
    ? candidates.filter((c) =>
        (c.title || "").toLowerCase().includes(filter.toLowerCase()) ||
        (c.source || "").toLowerCase().includes(filter.toLowerCase()))
    : candidates;

  const current = candidates.find((c) => c.id === currentId);

  return (
    <div data-testid="admin-iod-tab" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-2xl">La imagen del día</h3>
          <p className="text-sm text-sub max-w-xl mt-1">
            Selecciona <strong>una sola</strong> imagen para encabezar la portada. Se mostrará con atribución al medio y enlace a la nota original.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            placeholder="Filtrar título o medio…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent w-64"
            data-testid="iod-filter"
          />
          <button
            onClick={enrich}
            disabled={enriching}
            className="border border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors disabled:opacity-40"
            data-testid="iod-enrich"
            title="Vuelve a buscar la imagen Open Graph en notas que aún no tienen miniatura"
          >
            {enriching ? "Buscando…" : "↻ Refrescar imágenes"}
          </button>
          <button
            onClick={load}
            className="border border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
            data-testid="iod-reload"
          >
            ↻ Recargar
          </button>
        </div>
      </div>

      {msg && <p className="font-mono text-sm text-verde" data-testid="iod-msg">{msg}</p>}
      {err && <p className="font-mono text-sm text-rojo" data-testid="iod-err">{err}</p>}

      {/* Currently set */}
      {current && (
        <div className="border-2 border-rojo bg-paper p-4" data-testid="iod-current">
          <p className="font-mono text-[10px] uppercase tracking-widest text-rojo">Actual</p>
          <div className="mt-2 flex flex-col sm:flex-row gap-4">
            <img
              src={current.image_url}
              alt={current.title}
              className="w-full sm:w-64 h-auto object-cover border border-ink"
            />
            <div className="flex-1 space-y-2">
              <p className="font-serif-display text-xl leading-tight">{current.title}</p>
              <p className="text-xs font-mono uppercase tracking-widest text-sub">
                {current.source} · {(current.published_at || "").slice(0, 10)}
              </p>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-sub">Pie de foto editorial (opcional, máx 240)</span>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  maxLength={240}
                  rows={2}
                  placeholder="Si lo dejas vacío, usaremos el título de la nota."
                  className="mt-1 w-full bg-transparent border border-ink/40 focus:border-ink outline-none px-3 py-2 text-sm rounded-none"
                  data-testid="iod-caption"
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={updateCaption}
                  disabled={savingId === currentId}
                  className="border border-ink bg-ink text-paper px-3 py-1 font-mono text-[11px] uppercase tracking-widest hover:bg-rojo hover:border-rojo transition-colors disabled:opacity-40"
                  data-testid="iod-save-caption"
                >
                  Guardar pie
                </button>
                <button
                  onClick={clearIt}
                  className="border border-rojo text-rojo px-3 py-1 font-mono text-[11px] uppercase tracking-widest hover:bg-rojo hover:text-paper transition-colors"
                  data-testid="iod-clear"
                >
                  Quitar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidates grid */}
      <div className="border border-ink bg-paper">
        <div className="px-3 py-2 border-b border-ink bg-ink text-paper flex items-center justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest">
            Candidatos · últimas 36h ({filtered.length})
          </p>
        </div>
        {loading ? (
          <p className="px-3 py-4 text-sm text-sub" data-testid="iod-loading">Cargando candidatos…</p>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-4 text-sm text-sub">
            Sin notas con imagen aún. Haz clic en <strong>Refrescar imágenes</strong> para extraer las miniaturas Open Graph de las notas recientes.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-3" data-testid="iod-grid">
            {filtered.map((a) => {
              const isCurrent = a.id === currentId;
              return (
                <button
                  key={a.id}
                  onClick={() => setIt(a)}
                  disabled={savingId !== null}
                  className={`text-left border bg-paper transition-all hover:shadow-lg disabled:opacity-50 ${
                    isCurrent ? "border-rojo border-2 ring-2 ring-rojo/20" : "border-ink/30 hover:border-ink"
                  }`}
                  data-testid={`iod-card-${a.id}`}
                >
                  <div className="relative aspect-[16/10] bg-ink/5 overflow-hidden">
                    <img
                      src={a.image_url}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    {isCurrent && (
                      <span className="absolute top-2 left-2 bg-rojo text-paper font-mono text-[10px] uppercase tracking-widest px-2 py-0.5">
                        Actual
                      </span>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="font-serif-display text-sm leading-tight line-clamp-3">{a.title}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-sub">
                      {a.source}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
